/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, April 23rd 2022, 10:28:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useGlobalScope } from "core/providers";
import {
  useAPIFetchItem,
  FetchItemArgs,
  useAPIMutation,
  useAPIFetchList,
  UseFetchListArgs,
  useAPIRequest,
  APISERVICEPATHS,
  useAPIForm,
} from "lib/api";
import { User, UserValidationSchema, WorkProfile, WorkProfileValidationSchema } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";

const workersAPIServicePath = APISERVICEPATHS.workers;

export const useAPIFetchWorker = (
  workerId: PrimaryKeyValue<User>,
  fetchOptions: Omit<FetchItemArgs<User>, "apiServicePath" | "apiPathParams">
) =>
  useAPIFetchItem<User>({
    apiServicePath: workersAPIServicePath,
    apiPathParams: [workerId],
    ...fetchOptions,
  });

export const useAPIListWorkers = (args: Omit<UseFetchListArgs<User>, "apiServicePath">) =>
  useAPIFetchList<User>({
    apiServicePath: workersAPIServicePath,
    ...args,
  });

export const useAPIMutateWorker = () =>
  useAPIMutation<User>({
    apiServicePath: workersAPIServicePath,
  });

export const useWorkerAPIForm = (workerId: User["id"]) => {
  const [workerData, setWorkerData] = React.useState<User>({
    id: null,
    username: null,
    email: null,
    first_name: null,
    last_name: null,
    sites: [],
    registrationprofile: null,
  });

  const useWorkerAPIFormReturn = useAPIForm<User, User>({
    apiServicePath: workersAPIServicePath,
    apiFormDefaultValues: workerData,
    primaryKey: "id",
    apiFormDataValidationSchema: UserValidationSchema,
    apiInitialFetchId: workerId,
    titleKey: "username",
    formResetDependencyArray: [workerData?.id],
    setAPIFormResponse: setWorkerData,
  });

  return { useWorkerAPIFormReturn, workerData, setWorkerData };
};

const workProfilesAPIServicePath = APISERVICEPATHS.workProfiles;

export const useAPIGetWorkerProfile = (workerId: User["id"]) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const { makeRequest, ...remainingValues } = useAPIRequest<WorkProfile[], WorkProfile, WorkProfile>({
    apiServicePath: workProfilesAPIServicePath,
    makeRequestOnInitialMount: true,
  });

  const [workProfile, setWorkProfile] = React.useState<WorkProfile>();

  React.useEffect(() => {
    makeRequest({
      apiQueryParams: { user: workerId, organization: selectedOrganization?.id },
      setValidAPIResponse: (workProfileFilterResult: WorkProfile[]) => setWorkProfile(workProfileFilterResult?.[0]),
    });
  }, [makeRequest, selectedOrganization?.id, workerId]);

  return { workProfile, setWorkProfile, workProfileFetchState: remainingValues };
};

export const useWorkProfileAPIForm = (workerId: User["id"]) => {
  const { workProfile, setWorkProfile, workProfileFetchState } = useAPIGetWorkerProfile(workerId);

  const useWorkProfileAPIFormReturn = useAPIForm<WorkProfile, WorkProfile>({
    apiServicePath: workProfilesAPIServicePath,
    apiFormDefaultValues: workProfile,
    primaryKey: "id",
    apiFormDataValidationSchema: WorkProfileValidationSchema,
    apiInitialFetchId: null,
    formResetDependencyArray: [workProfile?.id], // This is not a bug my friend, reset the form when a team changes because multiple saves of a team could have the same details. So we reset everytime we get a new instance in memory. Reset needs to be done because of react-hook-form embracing uncontrolled inputs.
    setAPIFormResponse: setWorkProfile,
  });

  return { useWorkProfileAPIFormReturn, workProfile, workProfileFetchState };
};

export const useAPIChangeWorkerType = () =>
  useAPIMutation<User>({
    apiServicePath: APISERVICEPATHS.changeWorkerType,
  });

export const useAPIRemoveWorker = () => useAPIMutation<User>({ apiServicePath: APISERVICEPATHS.removeWorker });
